import React, { useState, useContext, useEffect } from 'react';

import TextInput from '@shared/component/TextInput';
import { AuthContext } from '@shared/context/AuthContext';
import APIService from '@shared/services/api';
import Notify from '@shared/notify';
import { NavLink, useNavigate } from 'react-router-dom';
import CheckValidator from '@shared/checkValidator';

export function ForgotContainer() {
    const [errors, setError] = useState<any>({
        phone: {
            required: true,
            error: true,
            message: 'Please enter phone number',
            pattern: /^[6-9]{1}[0-9]{9}$/,
            maxLength: 10,
        },
    });

    const [isSubmit, setIsSubmit] = useState(false);
    const [formData, setFormData] = useState<any>({});
    const [showLoader, setshowLoader] = useState(false);
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [expired, setExpired] = useState<boolean>(false);
    const [timer, setTimer] = useState<number>(30);

    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (timer > 0) {
            interval = setInterval(() => {
                setTimer(prevTimer => prevTimer - 1);
            }, 1000);
        } else {
            setExpired(true);
        }

        return () => clearInterval(interval);
    }, [timer]);

    const formatTime = (timeInSeconds: number): string => {
        const days = Math.floor(timeInSeconds / (60 * 60 * 24));
        const hours = Math.floor((timeInSeconds % (60 * 60 * 24)) / (60 * 60));
        const minutes = Math.floor((timeInSeconds % (60 * 60)) / 60);
        const seconds = timeInSeconds % 60;

        const formattedTime = [];
        if (days > 0) formattedTime.push(`${days}d`);
        if (hours > 0) formattedTime.push(`${hours}h`);
        if (minutes > 0) formattedTime.push(`${minutes}m`);
        formattedTime.push(`${seconds}s`);

        return formattedTime.join(' ');
    };

    const navigate = useNavigate();

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        try {
            e.preventDefault();

            const isValid = await CheckValidator(errors, formData);
            setIsSubmit(!isValid);
            if (isValid) {
                callApi();
            }
        } catch (error) {}
    };

    const getValue = (e: any) => {
        try {
            const data = { ...formData, ...e };
            setFormData(data);
        } catch (error) {}
    };

    const resendOtp = async () => {
        try {
            setshowLoader(true);
            const { data } = await APIService.post('user/forgot', formData, false, true);

            if (data.success) {
                setshowLoader(false);
                setIsSubmit(false);
                setTimer(30);
                setExpired(false);
                setShowOtpInput(true);
                Notify('success', data.message);
                setFormData({ ...formData, ['otp']: '' });
            } else {
                Notify('error', data.message);
            }
        } catch (error) {
            setshowLoader(false);

            Notify('error', error.response.data.message);
        } finally {
            setshowLoader(false);
        }
    };

    const callApi = async () => {
        try {
            //console.log(formData);
            setshowLoader(true);

            // Use to send otp
            if (!showOtpInput) {
                const { data } = await APIService.post('user/forgot', formData, false, true);

                if (data.success) {
                    setshowLoader(false);
                    setIsSubmit(false);
                    setTimer(30);
                    setShowOtpInput(true);
                    setExpired(false);
                    Notify('success', data.message);
                    setFormData({ ...formData, ['otp']: '' });
                    setError({
                        otp: {
                            required: true,
                            error: true,
                            message: 'Please enter otp',
                            pattern: /^(\d{6})$/,
                            maxLength: 6,
                        },
                    });
                } else {
                    setshowLoader(false);
                    Notify('error', data.message);
                }
            }

            // Use to verify OTP
            if (showOtpInput && !showPassword) {
                const { data } = await APIService.post('user/validateOtp', formData, false, true);

                if (data.success) {
                    setshowLoader(false);
                    setIsSubmit(false);

                    setShowPassword(true);
                    Notify('success', data.message);
                    setFormData({ ...formData, ['password']: '', ['confirmpassword']: '' });

                    setError({
                        password: {
                            required: true,
                            error: true,
                            message: 'Please enter password',
                            maxLength: 20,
                        },
                        confirmpassword: {
                            required: true,
                            error: true,
                            message: 'Please enter confirm password',
                            maxLength: 20,
                        },
                    });
                } else {
                    setshowLoader(false);
                    Notify('error', data.message);
                }
            }

            // Use to reset passowrd
            if (showPassword) {
                if (formData.password !== formData.confirmpassword) {
                    Notify('error', 'Password and confirm password are not same');
                    return;
                }

                const { data } = await APIService.post('user/resetPassword', formData, false, true);

                if (data.success) {
                    setshowLoader(false);
                    setIsSubmit(false);

                    setShowPassword(false);
                    setShowOtpInput(false);
                    Notify('success', data.message);

                    setFormData({ ['phone']: '', ['otp']: '', ['password']: '', ['confirmpassword']: '' });
                    navigate('/');
                } else {
                    setshowLoader(false);
                    Notify('error', data.message);
                }
            }
        } catch (error) {
            setshowLoader(false);

            Notify('error', error.response.data.message);
        } finally {
            setshowLoader(false);
        }
    };

    return (
        <>
            <main>
                <div className="form_outer_bg">
                    {showLoader && <div className="loader"></div>}
                    <div className="form_box_cover login_cover">
                        <div className="text_box">
                            <h2>Forgot Password</h2>
                            <p>
                                If {"you've"} forgotten your password, {"don't"} worry. Provide the details you used
                                during registration
                            </p>
                        </div>
                        <form onSubmit={onSubmit} noValidate>
                            {!showOtpInput ? (
                                <TextInput
                                    data={{ type: 'number', placeholder: 'Phone Number', name: 'Phone' }}
                                    isRequired={true}
                                    controlName="phone"
                                    isSubmit={isSubmit}
                                    value={formData?.phone}
                                    setValue={(e: any) => getValue(e)}
                                    errorMsg={errors?.phone?.message || ''}
                                    inputProps={errors}
                                />
                            ) : (
                                <>
                                    {' '}
                                    {!showPassword ? (
                                        <>
                                            <p className="text-start text-success" style={{ fontSize: '14px' }}>
                                                OTP sent to {formData?.phone}
                                            </p>
                                            <TextInput
                                                data={{ type: 'number', placeholder: 'OTP', name: 'otp' }}
                                                isRequired={true}
                                                controlName="otp"
                                                value={formData?.otp}
                                                isSubmit={isSubmit}
                                                setValue={(e: any) => getValue(e)}
                                                errorMsg={errors?.otp?.message || ''}
                                                inputProps={errors}
                                            />
                                            {expired ? (
                                                <p className="text-end text-danger" style={{ fontSize: '13px' }}>
                                                    <span
                                                        className="text-secondary"
                                                        style={{ fontSize: '13px', cursor: 'pointer' }}
                                                        onClick={() => {
                                                            resendOtp();
                                                        }}
                                                    >
                                                        <u>Resend Otp</u>
                                                    </span>
                                                </p>
                                            ) : (
                                                <p className="text-end text-secondary" style={{ fontSize: '13px' }}>
                                                    Resend Otp in {formatTime(timer)}
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {' '}
                                            {!showPassword ? (
                                                <>
                                                    <p className="text-start text-success" style={{ fontSize: '14px' }}>
                                                        OTP sent to {formData?.phone}
                                                    </p>
                                                    <TextInput
                                                        data={{ type: 'number', placeholder: 'OTP', name: 'otp' }}
                                                        isRequired={true}
                                                        controlName="otp"
                                                        value={formData?.otp}
                                                        isSubmit={isSubmit}
                                                        setValue={(e: any) => getValue(e)}
                                                        errorMsg={errors?.otp?.message || ''}
                                                        inputProps={errors}
                                                    />
                                                    {expired ? (
                                                        <p
                                                            className="text-end text-danger"
                                                            style={{ fontSize: '13px' }}
                                                        >
                                                            <span
                                                                className="text-secondary"
                                                                style={{ fontSize: '13px', cursor: 'pointer' }}
                                                                onClick={() => {
                                                                    resendOtp();
                                                                }}
                                                            >
                                                                <u>Resend Otp</u>
                                                            </span>
                                                        </p>
                                                    ) : (
                                                        <p
                                                            className="text-end text-secondary"
                                                            style={{ fontSize: '13px' }}
                                                        >
                                                            Resend Otp in {formatTime(timer)}
                                                        </p>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <TextInput
                                                        data={{
                                                            type: 'password',
                                                            placeholder: 'Password',
                                                            name: 'password',
                                                        }}
                                                        controlName="password"
                                                        value={formData?.password}
                                                        isSubmit={isSubmit}
                                                        setValue={(e: any) => getValue(e)}
                                                        errorMsg={errors?.phone?.message || ''}
                                                        inputProps={errors}
                                                    />
                                                    <TextInput
                                                        data={{
                                                            type: 'password',
                                                            placeholder: 'Confirm password',
                                                            name: 'confirmpassword',
                                                        }}
                                                        controlName="confirmpassword"
                                                        value={formData?.confirmpassword}
                                                        isSubmit={isSubmit}
                                                        setValue={(e: any) => getValue(e)}
                                                        errorMsg={errors?.confirmpassword?.message || ''}
                                                        formData={formData}
                                                        inputProps={errors}
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            )}

                            <input className="submit_btn" type="submit" name="" value="Submit" />
                            <NavLink to="/">
                                <input
                                    className="submit_btn"
                                    type="button"
                                    style={{
                                        borderRadius: '42px',
                                        border: '1px solid #E8E9EC',
                                        background: '#FFF',
                                        color: '#16171A',
                                    }}
                                    name=""
                                    value="Back to Sign in"
                                />
                            </NavLink>
                            {/* <button className="submit_btn" type='submit'>Sign in</button> */}
                        </form>
                    </div>
                </div>
            </main>
            <footer>
                <div className="reg_footer_cover">
                    <p>Rcm E-bidding 2023</p>
                </div>
            </footer>
        </>
    );
}
